import { ReactNode } from 'react';
import { SelectTableProps } from '../../SelectTable';
import { FORMITEM_WIDTH_ENUM } from '../../constants/constant';
import SearchForm from '../../SearchForm';
import { SuperFormItemProps } from '../interface';
import { SuperSelectTableProps } from '../../SuperSelectTable/interface';
import { SelectInputSelectProps } from '../../SearchForm/selectInput';

export const FORMITEM_WIDTH = {
  Button: FORMITEM_WIDTH_ENUM.small,
  Input: FORMITEM_WIDTH_ENUM.default,
  AutoComplete: FORMITEM_WIDTH_ENUM.big,
  Address: FORMITEM_WIDTH_ENUM.big,
  InputInterval: FORMITEM_WIDTH_ENUM.long,
  InputNumber: FORMITEM_WIDTH_ENUM.default,
  InputSelect: FORMITEM_WIDTH_ENUM.big,
  Select: FORMITEM_WIDTH_ENUM.default,
  SelectTab: FORMITEM_WIDTH_ENUM.default,
  SuperSelect: FORMITEM_WIDTH_ENUM.default,
  TreeSelect: FORMITEM_WIDTH_ENUM.big,
  SelectInput: FORMITEM_WIDTH_ENUM.big,
  Checkbox: FORMITEM_WIDTH_ENUM.default,
  CheckboxGroup: FORMITEM_WIDTH_ENUM.big,
  SelectTable: FORMITEM_WIDTH_ENUM.biger,
  SuperSelectTable: FORMITEM_WIDTH_ENUM.biger,
  SelectGroup: FORMITEM_WIDTH_ENUM.default,
  AssociateSelect: FORMITEM_WIDTH_ENUM.big,
  AutoMatch: FORMITEM_WIDTH_ENUM.big,
  TextArea: FORMITEM_WIDTH_ENUM.big,
  TextAreaRow: FORMITEM_WIDTH_ENUM.biger,
  DatePicker: FORMITEM_WIDTH_ENUM.big,
  RangePicker: FORMITEM_WIDTH_ENUM.long,
  TimePicker: FORMITEM_WIDTH_ENUM.default,
  TimeSlot: FORMITEM_WIDTH_ENUM.long,
  RadioGroup: FORMITEM_WIDTH_ENUM.big,
  Combination: FORMITEM_WIDTH_ENUM.long,
  SelectCustom: FORMITEM_WIDTH_ENUM.long,
  ZoneTime: FORMITEM_WIDTH_ENUM.longer,
  SelectGroupTable: FORMITEM_WIDTH_ENUM.long,
  Cascader: FORMITEM_WIDTH_ENUM.big,
  GroupSearch: 280,
  FieldGroupSearch: FORMITEM_WIDTH_ENUM.longer,
};

function getFormItem(formItem: SuperFormItemProps, key: number | string) {
  const {
    formItemType,
    buttonProps,
    inputProps,
    inputNumberProps,
    selectProps,
    selectTabProps,
    superSelectProps,
    selectTableProps,
    superSelectTableProps,
    selectGroupProps,
    switchProps,
    preFormItemProps,
    textAreaProps,
    datePickerProps,
    rangePickerProps,
    timePickerProps,
    uploadProps,
    startInputNumberProps,
    endInputNumberProps,
    beforeFormItemProps,
    afterFormItemProps,
    checkboxGroupProps,
    checkboxProps,
    associateSelectProps,
    autoMatchProps,
    treeSelectProps,
    textAreaRowProps,
    radioGroupProps,
    cascaderProps,
    zoneProps,
    zoneTimeProps,
    autoCompleteProps,
    afterSelectTableProps,
    groupSearchProps,
    fieldGroupSearchProps,
    combinationItem,
    render: renderCopy,
    renderDisabled,
    tabsProps,
    tabPanes,
    children,
    middleText,
    disabled,
    width,
    style = { paddingLeft: 0, paddingRight: 0 },
    span,
    labelCol,
    ...resProps
  } = formItem;
  const render = renderCopy || renderDisabled;
  const baseFormItemProps =
    disabled !== undefined
      ? { disabled, style: { width: '100%' } }
      : { style: { width: '100%' } };
  const formItemName = resProps.name;
  const formItemKey = typeof formItemName === 'string' ? formItemName : key;
  resProps.hideLabel = true;
  switch (formItemType) {
    case 'Button':
      return (
        <SearchForm.Button
          key={formItemKey}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          buttonProps={{ ...baseFormItemProps, ...buttonProps }}
        >
          {children as ReactNode}
        </SearchForm.Button>
      );
    case 'Input':
      return (
        <SearchForm.Input
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          inputProps={{ ...baseFormItemProps, ...inputProps }}
        />
      );
    case 'Checkbox':
      return (
        <SearchForm.Checkbox
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          checkboxProps={{ ...baseFormItemProps, ...checkboxProps }}
        />
      );
    case 'CheckboxGroup':
      return (
        <SearchForm.CheckboxGroup
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          checkboxGroupProps={{ ...baseFormItemProps, ...checkboxGroupProps }}
        />
      );
    case 'AutoComplete':
      return (
        <SearchForm.AutoComplete
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          autoCompleteProps={{ ...baseFormItemProps, ...autoCompleteProps }}
        />
      );
    case 'GroupSearch': 
      return (
        <SearchForm.GroupSearch
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          groupSearchProps={groupSearchProps}
        />
      );
    case 'FieldGroupSearch': 
      return (
        <SearchForm.FieldGroupSearch
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          fieldGroupSearchProps={fieldGroupSearchProps}
        />
      );
    case 'InputInterval':
      return (
        <SearchForm.InputInterval
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          startInputNumberProps={{
            ...baseFormItemProps,
            ...startInputNumberProps,
          }}
          disabled={disabled}
          endInputNumberProps={{ ...baseFormItemProps, ...endInputNumberProps }}
          preFormItemProps={preFormItemProps}
          afterFormItemProps={afterFormItemProps}
          middleText={middleText}
        />
      );
    case 'SelectCustom':
      const options = resProps.options;
      if (Array.isArray(options) && options.length) {
        const firstOption = options[0];
        return (
          <SearchForm.SelectCustom
            key={formItemKey}
            render={render}
            style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
            {...resProps}
            selectProps={selectProps}
            beforeFormItemProps={{
              initialValue: firstOption.value,
              ...beforeFormItemProps,
            }}
            afterFormItemProps={afterFormItemProps}
            disabled={disabled}
            middleText={middleText}
          />
        );
      }
      return null;
    case 'InputNumber':
      return (
        <SearchForm.InputNumber
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          inputNumberProps={{ ...baseFormItemProps, ...inputNumberProps }}
        />
      );
    case 'InputSelect':
      return (
        <SearchForm.InputSelect
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          selectProps={{ ...baseFormItemProps, ...selectProps }}
          inputProps={{ ...baseFormItemProps, ...inputProps }}
          afterFormItemProps={afterFormItemProps}
        />
      );
    case 'Select':
      return (
        <SearchForm.Select
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          selectProps={{ ...baseFormItemProps, ...selectProps }}
        />
      );
    case 'SelectTab':
      return (
        <SearchForm.SelectTab
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          selectTabProps={{ ...baseFormItemProps, ...selectTabProps }}
        />
      );
    case 'SuperSelect':
      return (
        <SearchForm.SuperSelect
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          superSelectProps={{ ...baseFormItemProps, ...superSelectProps }}
        />
      );
    case 'TreeSelect':
      return (
        <SearchForm.TreeSelect
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          treeSelectProps={{
            maxTagCount: 1,
            ...baseFormItemProps,
            ...treeSelectProps,
          }}
        />
      );
    case 'Cascader':
      return (
        <SearchForm.Cascader
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          cascaderProps={{...baseFormItemProps, ...cascaderProps,}}
        />
      );
    case 'SelectInput':
      return (
        <SearchForm.SelectInput
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          selectProps={{ ...baseFormItemProps, ...selectProps } as SelectInputSelectProps}
          inputProps={{ ...baseFormItemProps, ...inputProps }}
          preFormItemProps={preFormItemProps}
        />
      );
    case 'ZoneTime':
      return (
        <SearchForm.ZoneTime
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          zoneProps={zoneProps}
          zoneTimeProps={zoneTimeProps}
          disabled={disabled}
        />
      );
    case 'SelectTable':
      return (
        <SearchForm.SelectTable
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          selectTableProps={{  
            maxTagTextLength: 8,
            ...baseFormItemProps,
            ...selectTableProps,
          } as SelectTableProps}
        />
      );
    case 'SuperSelectTable':
      return (
        <SearchForm.SuperSelectTable
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          superSelectTableProps={{
            ...baseFormItemProps,
            ...superSelectTableProps,
          } as SuperSelectTableProps}
        />
      );
    case 'SelectGroup':
      return (
        <SearchForm.SelectGroup
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          selectGroupProps={{ ...baseFormItemProps, ...selectGroupProps }}
        />
      );
    case 'SelectGroupTable':
      return (
        <SearchForm.SelectGroupTable
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          beforeFormItemProps={beforeFormItemProps}
          afterFormItemProps={afterFormItemProps}
          afterSelectTableProps={{...baseFormItemProps, ...afterSelectTableProps} as SelectTableProps}
        />
      );
    case 'AssociateSelect':
      return (
        <SearchForm.AssociateSelect
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          associateSelectProps={{
            ...baseFormItemProps,
            ...associateSelectProps,
          }}
        />
      );
    case 'AutoMatch':
      return (
        <SearchForm.AutoMatch
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          autoMatchProps={{
            ...baseFormItemProps,
            ...autoMatchProps,
          }}
        />
      );
    case 'Operation':
      return <SearchForm.Operation key={formItemKey} {...resProps} />;
    case 'TextArea':
      return (
        <SearchForm.TextArea
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          textAreaProps={{ rows: 1, ...baseFormItemProps, ...textAreaProps }}
        />
      );
    case 'TextAreaRow':
      return (
        <SearchForm.TextAreaRow
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          textAreaRowProps={{
            maxTagTextLength: 8,
            ...baseFormItemProps,
            ...textAreaRowProps,
          }}
        />
      );
    case 'DatePicker':
      return (
        <SearchForm.DatePicker
          key={formItemKey}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          datePickerProps={{ ...baseFormItemProps, ...datePickerProps }}
        />
      );
    case 'RangePicker':
      return (
        <SearchForm.RangePicker
          key={formItemKey}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          rangePickerProps={{ ...baseFormItemProps, ...rangePickerProps }}
        />
      );
    case 'TimePicker':
      return (
        <SearchForm.TimePicker
          key={formItemKey}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          timePickerProps={{ ...baseFormItemProps, ...timePickerProps }}
        />
      );
    case 'TimeSlot':
      return (
        <SearchForm.TimeSlot
          key={formItemKey}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          datePickerProps={{ ...baseFormItemProps, ...datePickerProps }}
        />
      );
    case 'RadioGroup':
      return (
        <SearchForm.RadioGroup
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          {...resProps}
          radioGroupProps={{ ...baseFormItemProps, ...radioGroupProps }}
        />
      );
    case 'Combination':
      return (
        <SearchForm.Combination
          key={formItemKey}
          render={render}
          disabled={disabled}
          style={{ width: width || FORMITEM_WIDTH[formItemType], ...style }}
          middleText={middleText}
          {...resProps}
          combinationItem={combinationItem}
        />
      );
    case 'Other':
      return (
        <SearchForm.Other key={formItemKey} {...resProps} render={render} />
      );
    default:
      return (
        <SearchForm.Input
          key={formItemKey}
          render={render}
          style={{ width: width || FORMITEM_WIDTH.Input, ...style }}
          {...resProps}
          inputProps={{ ...baseFormItemProps, ...inputProps }}
        />
      );
  }
}

function useSearchForm(formItems: SuperFormItemProps[], disabled?: boolean) {
  let renderChildren: any[] = [];
  if (Array.isArray(formItems)) {
    renderChildren = formItems?.map?.((formItem, index) =>
      getFormItem({ ...formItem, disabled }, index),
    );
  }
  return renderChildren;
}

export default useSearchForm;
